import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { FormattedMessage, useIntl } from 'react-intl';

import type { ToolCallDisplay } from '../../../contracts/tool-call-display';
import { TOOL_PRESENTATION_REGISTRY } from '../../../data/projects/timeline/presentation/tool-registry';
import type { BlockStatus } from '../../../data/projects/timeline/presentation/types';
import { inlineToolCallLabel } from './inlineToolCallLabel';
import { ShimmerText } from './ShimmerText';
import { toolIcon } from './toolPresentation';
import { useWorkingRowText } from './useWorkingRowText';

import type { JSX } from 'react';

const ATOM_ANIMATE_IN =
  'motion-safe:duration-300 motion-safe:ease-out-quart motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1';

export interface InlineToolCallRowProps {
  toolName: string;
  toolInput: Record<string, unknown>;
  status: BlockStatus;
  /** Backend-owned display; its title is preferred over the raw-name humanizer. */
  toolCallDisplay?: ToolCallDisplay;
  /** When set, the row is a button and shows a trailing open label (artifact open). */
  onOpen?: () => void;
  /** Accessible name for the open action, e.g. "Open session". */
  openLabel?: string;
  openTestId?: string;
}

/**
 * Borderless inline tool step — adapted from Ronnie's demo
 * (`ChatStepsView.computerUseInline.tsx` → `ToolCallRow`): small icon +
 * muted one-liner whose tense follows status; every in-flight row
 * shimmers until complete. Used by the generic tool path only; bespoke
 * adapters keep their own presentation.
 */
export const InlineToolCallRow = ({
  toolName,
  toolInput,
  status,
  toolCallDisplay,
  onOpen,
  openLabel,
  openTestId,
}: InlineToolCallRowProps): JSX.Element => {
  const intl = useIntl();
  const rowText = useWorkingRowText();
  const Icon = toolIcon(toolName);
  const label = inlineToolCallLabel(intl, {
    toolName,
    status,
    input: toolInput,
    toolCallDisplay,
  });
  const isRunning = status === 'running';
  const isOpenable = !!onOpen && status === 'success';
  const catalogOnly =
    TOOL_PRESENTATION_REGISTRY[toolName]?.label?.textMode === 'catalog-only';
  const detail = catalogOnly ? undefined : toolCallDisplay?.detail?.trim();
  // A failure keeps the ordinary grey: the words carry it, and the answer is where it is reported.
  const tint = 'text-text-tertiary';

  const labelClass = cn(rowText, 'flex-1', tint);

  const labelNode = (
    <TypographyP2 className={labelClass}>
      {isRunning ? <ShimmerText className="inline">{label}</ShimmerText> : label}
    </TypographyP2>
  );

  const iconNode = <Icon className={cn('size-3.5 shrink-0', tint)} aria-hidden />;

  const openAffordanceNode = isOpenable ? (
    <TypographyCaption
      className={cn(
        // In flow, not absolute. WKWebView shrink-wraps a button flex item, so
        // an `end-0` overlay lands on the tool name in the desktop app.
        'shrink-0 whitespace-nowrap text-text-tertiary underline-offset-2',
        'opacity-0 transition-opacity duration-150',
        'group-hover/inline-tool:opacity-100 group-focus-visible/inline-tool:opacity-100',
        'hover:underline'
      )}
    >
      <FormattedMessage
        defaultMessage="Open"
        id="PhAnDCZ4mz"
        description="Action to open the artifact from an inline tool call"
      />
    </TypographyCaption>
  ) : null;

  const detailNode = detail ? (
    <TypographyCaption
      className={cn(
        'line-clamp-3 min-w-0 break-words',
        // Icon (size-3.5) + gap-2, so the caption aligns with the label. A
        // literal because `theme(spacing.3.5)` does not resolve (dotted key).
        'ps-[1.375rem]',
        tint
      )}
    >
      {detail}
    </TypographyCaption>
  ) : null;

  const shellClass = cn(
    'block w-full min-w-0 text-start',
    isRunning && ATOM_ANIMATE_IN,
    isOpenable &&
      'group/inline-tool cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus'
  );

  const body = (
    <div className="flex w-full min-w-0 flex-col">
      {/* Open stays on this row, beside the label. A detail line below must
          not share that row, and the label must be able to shrink before
          Open does. */}
      <div className="flex min-w-0 items-center gap-2">
        {iconNode}
        {labelNode}
        {openAffordanceNode}
      </div>
      {detailNode}
    </div>
  );

  if (isOpenable) {
    const accessibleLabel = openLabel
      ? intl.formatMessage(
          {
            defaultMessage: '{action}: {label}',
            id: 'nWTmZ1yd0l',
            description:
              'Accessible name for an inline tool call; action is an already translated open action and label is the tool result',
          },
          { action: openLabel, label }
        )
      : intl.formatMessage(
          {
            defaultMessage: 'Open: {label}',
            id: 'YdMJ0RcGbK',
            description: 'Accessible name for opening the result of an inline tool call',
          },
          { label }
        );

    return (
      // A plain box stretches in WKWebView; a button flex item does not, which
      // is what jammed Open onto the label in the desktop app.
      <div className="w-full min-w-0 self-stretch">
        <button
          type="button"
          onClick={onOpen}
          className={shellClass}
          data-testid={openTestId ?? 'inline-tool-call-row'}
          aria-label={accessibleLabel}
        >
          {body}
        </button>
      </div>
    );
  }

  return (
    <div className={shellClass} data-testid="inline-tool-call-row">
      {body}
    </div>
  );
};
